import { useCallback, useRef } from 'react';

/**
 * 派生した Set の参照を、中身が変わっていない間は据え置く。
 *
 * `useMemo` の中で呼び、戻り値を使う:
 *
 * ```ts
 * const keepIds = useKeepSetIdentity<string>();
 * const ids = useMemo(() => keepIds(buildIds(rowGroups)), [rowGroups, keepIds]);
 * ```
 *
 * 派生元（行グループなど）の参照が変わると `useMemo` は必ず作り直すので、中身が同じでも
 * 毎回新しい Set が下へ流れ、それを props に持つコンポーネントの `memo` が破れる。
 *
 * 使用条件: 判定 (`isSameSet`) は**順序を見ない**ので、順序に意味がある集合には使えない
 * （並び順を読む相手には並びの変化が届かなくなる）。現在の呼び出し先はどれも `.has` で
 * 引くだけ（duplicateStoreNames / routeDeviationCardIds）。順序も見たいなら
 * `useKeepArrayIdentity` を使う。
 *
 * Why not 呼び出し側で ref を直接持つ: 前回値を render 中に読み書きする形なので、
 * 書き方を各所で真似ると片方だけ更新を忘れる。1 か所に閉じてテストで両方向を固定する。
 *
 * Why not render 中の ref 書き込みを避ける: 返すのは常に `next` と**値が等価**なものだけ
 * なので、React が捨てた render（StrictMode の二重呼び出し、中断された render）が
 * ref を汚しても壊れるものが無い。汚れた ref から返るのは「中身の等しい別インスタンス」で、
 * 変わるのは identity だけ — そして identity を据え置くことがこの hook の目的そのもの。
 */
export function useKeepSetIdentity<T>(): (next: Set<T>) => Set<T> {
  const previousRef = useRef<Set<T> | null>(null);

  return useCallback((next: Set<T>) => {
    const previous = previousRef.current;
    if (previous && isSameSet(previous, next)) return previous;
    previousRef.current = next;
    return next;
  }, []);
}

/**
 * 派生した配列の参照を、要素が変わっていない間は据え置く。使い方と render 中の ref 書き込みが
 * 安全な理由は `useKeepSetIdentity` と同じ。判定はこちらは**並び順も見る**。
 *
 * dnd-kit の `SortableContext` は `items` prop を**参照**で依存比較する
 * (@dnd-kit/sortable 8.0.0 の sortable.esm.js:308)。中身が同じでも配列が毎回別物だと
 * context の値が毎描画で変わり、配下の `useSortable` を呼ぶ行が `memo` を無視して
 * 描き直される（VehicleRowCards.tsx の sortableIds のコメントと同じ機構）。
 */
export function useKeepArrayIdentity<T>(): (next: T[]) => T[] {
  const previousRef = useRef<T[] | null>(null);

  return useCallback((next: T[]) => {
    const previous = previousRef.current;
    if (previous && isSameArray(previous, next)) return previous;
    previousRef.current = next;
    return next;
  }, []);
}

/** 中身が同じ集合か。要素数と各要素の有無だけを見る（順序は見ない） */
function isSameSet<T>(a: ReadonlySet<T>, b: ReadonlySet<T>): boolean {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
}

/** 同じ並びで同じ要素か。要素は `===` で比べる */
function isSameArray<T>(a: readonly T[], b: readonly T[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
}
